'use client';

import type {
  PageType,
  PageViewDestination,
  Placement,
  ProductType,
} from '@hc-frontend/core-types';
import { useEventBus, useReferrer } from '@hc-frontend/core-ui';
import type {
  SendMetricEvent,
  SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import { useEffect } from 'react';

interface PageViewMetricInput {
  placement: Placement;
  product: ProductType;
  destination: PageViewDestination;
  pageType?: PageType;
  save?: boolean;
}

interface PageVisitContext {
  hostname: string;
  hostnameAndPath: string;
  referrer: string | undefined;
}

function buildTags({
  placement,
  destination,
  pageType,
  product,
  hostname,
  hostnameAndPath,
  referrer,
}: PageViewMetricInput & PageVisitContext) {
  const tags = {
    placement,
    destination,
    domain: hostname,
    pageUrl: hostnameAndPath,
    platform: 'apollo',
    product,
    referrer: referrer ?? document.referrer.split('?')[0],
  };
  return pageType
    ? {
        ...tags,
        pageType,
      }
    : tags;
}

export function PageViewMetric({
  placement,
  destination,
  product,
  pageType,
  save = true,
}: PageViewMetricInput) {
  const eventBus = useEventBus();

  const { referrer } = useReferrer();

  useEffect(() => {
    const { hostname, pathname, protocol, search } = window.location;
    const hostnameAndPath = `${protocol}//${hostname}${pathname}`;

    if (save) {
      window.sessionStorage.setItem('SAVED_TYP', pathname + search);
    }

    const tags = buildTags({
      placement,
      destination,
      pageType,
      product,
      hostname,
      hostnameAndPath,
      referrer,
    });

    eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
      id: 'page-view',
      topic: 'metric.client.message',
      source: hostnameAndPath,
      metric: {
        name: 'page-view',
        tags: tags,
      },
    });
  }, [eventBus, destination, placement, product, referrer, pageType, save]);

  return null;
}
